import { Steps } from "primereact/steps";
import { useState, useEffect, useCallback, useRef } from "react";
import ReactMarkdown from 'react-markdown';

const stepDetails = [
  { title: "DV–IVs Configuration" },
  { title: "Symbolic Encryption" },
  { title: "One-Click Training" },
];

export default function Training() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideType, setSlideType] = useState("md");
  const [currentSlide, setCurrentSlide] = useState({"num":1,"type":"md"});
  const [mdContent, setMdContent] = useState("");
  const [availableSlides, setAvailableSlides] = useState<any[]>([]); // 存储当前步骤存在的 slide 编号
  const [previewImg, setPreviewImg] = useState<string | null>(null);

  const CONTENT_HEIGHT = 300;

  // 核心：探测当前步骤下有多少个 slide
  const checkAvailableSlides = useCallback(async (stepIdx: number) => {
    const validSlides: any[] = [];
    
    for (let i = 1; i <= 10; i++) {
      try {
        // 这里的关键是：获取响应对象并检查其内容类型
        const mdRes = await fetch(`/training/step${stepIdx + 1}/s${i}.md`);
        const imgRes = await fetch(`/training/step${stepIdx + 1}/s${i}.png`);
  
        const hasMd = mdRes.ok && mdRes.headers.get("content-type")?.includes("text/markdown");
        // 注意：有些服务器可能把 md 识别为 text/plain
        const isPlainText = mdRes.ok && mdRes.headers.get("content-type")?.includes("text/plain");
        const hasImg = imgRes.ok && imgRes.headers.get("content-type")?.includes("image/");
  
        // 如果返回的是 text/html，说明命中了前端路由回退，判定为不存在
        if ((hasMd || isPlainText || hasImg)) {
          let param: Record<string, any> = {};
          param["num"]=i;
          if(hasImg){
            param["type"]="img";
          }else{
            param["type"]="md";
          }
          validSlides.push(param);
        } else {
          // 如果 s1 都不存在或者断开了，就停止探测
          break;
        }
      } catch (e) {
        break;
      }
    }
    setAvailableSlides(validSlides);
  }, []);

  // 当步骤切换时，探测 slide 数量并重置到第 1 个
  useEffect(() => {
    checkAvailableSlides(activeIndex);
    setCurrentSlide({"num":1,"type":"img"});
  }, [activeIndex, checkAvailableSlides]);

  // 当步骤或 Slide 变化时，加载文字内容
  useEffect(() => {
    const fetchMd = async () => {
      setMdContent(""); // 清空旧内容防止闪烁
      try {
        if(currentSlide.type==="md"){
          setSlideType("md");
          const response = await fetch(`/training/step${activeIndex + 1}/s${currentSlide.num}.md`);
          if (response.ok) {
            const text = await response.text();
            setMdContent(text);
          }
        }else{
          setSlideType("img");
        }
      } catch (err) {
        setMdContent("");
      }
    };
    fetchMd();
  }, [activeIndex, currentSlide]);

  const items = stepDetails.map((s, i) => ({
    label: s.title,
    command: () => setActiveIndex(i),
  }));

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPreviewImg(null);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);


  //实现鼠标离开左右两边才恢复原样
  const [isExpanded, setIsExpanded] = useState(false);  
  // 💡 把 Ref 绑在内部的真实内容上，而不是外层动画壳子上
  const innerContentRef = useRef<HTMLDivElement>(null);
  const [innerRefHeight, setInnerRefHeight] = useState(0);
  // 实时监测内部 Markdown 纯文本到底有多高
  useEffect(() => {
    if (innerContentRef.current) {
      setInnerRefHeight(innerContentRef.current.scrollHeight);
    }
  }, [mdContent]);
  const handleMouseEnter = () => {
    setIsExpanded(true);
  };
  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    if (!container) return;
    const offsetX = e.nativeEvent.offsetX;
    const containerWidth = container.offsetWidth;
    // 纯物理方向判断
    const exitedLeft = offsetX <= 5;
    const exitedRight = offsetX >= (containerWidth - 5);
    if (exitedLeft || exitedRight) {
      setIsExpanded(false);
    }
  };
  // 判断内部文本是否真的超过了限高
  const hasMoreContent = innerRefHeight > CONTENT_HEIGHT;  
  // 计算卡片应该展示的高度：
  // 1. 如果没超过限高，那永远就是内部高度（innerRefHeight），不需要变高。
  // 2. 如果超过了限高：展开时变成真实的 innerRefHeight，收起时卡死在 CONTENT_HEIGHT。
  const currentHeight = hasMoreContent 
    ? (isExpanded ? innerRefHeight : CONTENT_HEIGHT) 
    : innerRefHeight;



  // 重新聚焦到section顶部
  // 1. 创建一个绑定给 Section 顶部的 Ref
  const sectionRef = useRef<HTMLDivElement>(null);
  // 2. 封装一个点击处理函数
  const handleSlideChange = (slide:any) => {
    // 首先：切换当前的数据
    setCurrentSlide(slide);
    // 其次：让整个 Section 滚动到视口顶部
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: "smooth", // 平滑滚动，如果想要瞬间移动可以改成 "auto"
        block: "start",     // 聚焦到该元素的顶部
      });
    }
  };
  
  return (
    <section ref={sectionRef} id="training" className="section" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
      <h2 style={{ fontSize: 36, textAlign: "center", marginBottom: "2.5rem", fontWeight: 600 }}>
      Generate Symbolic Model
      </h2>
      <p
        style={{
          fontSize: "1.25rem",
          lineHeight: 1.7,
          color: "#666",
          fontWeight: 400,
          maxWidth: "800px",
          margin: "0 auto",
          marginBottom: "30px"
        }}
      >
        Powered by our proprietary Deep Symbolic Learning technology, your private data never leaves your device while generating a high-performance Symbolic Model in a single click.
      </p>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <Steps
          model={items}
          activeIndex={activeIndex}
          readOnly={false}
          onSelect={(e) => setActiveIndex(e.index)}
        />
        <div style={{ height: 4, background: "#eee", borderRadius: 999, marginTop: 12, overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${((activeIndex + 1) / stepDetails.length) * 100}%`,
              background: "#111",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      <div
        style={{
          maxWidth: 1000,
          margin: "0rem auto 0",
          padding: "0px",
          borderRadius: 16,
          border: "1px solid #eee",
          background: "#fff",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "2rem",
            alignItems: "start",
          }}
        >
          {/* TEXT SIDE */}
          {slideType==="md"&&(
              <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              style={{
                height: currentHeight || CONTENT_HEIGHT, // 加上兜底                  
                // 只有当内容确实超长、且处于展开状态时，才给大阴影；否则不加阴影或给极淡的阴影
                boxShadow: (hasMoreContent && isExpanded) ? '0 12px 30px rgba(0,0,0,0.1)' : 'none',
                // 丝滑动画
                transition: "height 0.35s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.3s ease",
                
                // 你的原有基础样式
                overflow: "hidden",
                position: "relative",
                background: "#fff",
                zIndex: 10,
                borderRadius: "8px",               
                
              }}
            >
              <div ref={innerContentRef} style={{ width: '100%', overflow: 'hidden' }}>
                <div 
                  className="markdown-body" style={{ padding: "10px",fontSize: 15, lineHeight: 1.8, color: "#444", paddingTop:"0px", marginTop:"0px" }}
                >
                  <ReactMarkdown>{mdContent}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}
          {/* IMAGE SIDE */}
          {slideType==="img"&&(
            <div
              style={{
                height: CONTENT_HEIGHT,
                borderRadius: 12,
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
                <img
                  key={`${activeIndex}-${currentSlide.num}`}
                  src={`/training/step${activeIndex + 1}/s${currentSlide.num}.png`}
                  alt=""
                  onClick={() => {
                    setPreviewImg(`/training/step${activeIndex + 1}/s${currentSlide.num}.png`)
                  }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  onLoad={(e) => { (e.target as HTMLImageElement).style.display = 'block'; }}
                  style={{
                    height: "100%",
                    width: "auto",
                    objectFit: "contain",
                    cursor: "zoom-in",
                  }}
                />
            </div>
          )}
        </div>

        {/* 动态渲染按钮：仅显示存在的 Slide */}
        <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "30px" }}>
          {availableSlides.map((slide) => (
            <button
              key={slide.num}
              onClick={() => handleSlideChange(slide)}
              style={{
                width: 32,
                height: 32,
                borderRadius: "6px",
                border: "1px solid #eee",
                background: currentSlide.num === slide.num ? "#111" : "#fff",
                color: currentSlide.num === slide.num ? "#fff" : "#666",
                cursor: "pointer",
                fontWeight: currentSlide.num === slide.num ? "bold" : "normal"
              }}
            >
              {slide.num}
            </button>
          ))}
        </div>
      </div>
      {previewImg && (
        <div
          onClick={() => setPreviewImg(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <img
            alt=""
            src={previewImg}
            onClick={() => setPreviewImg(null)} 
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: 12,
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              animation: "zoomIn 0.25s ease",
            }}
          />
        </div>
      )}
    </section>
  );
  
}


